import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import {
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  maxLength,
  email,
  AutocompleteInput,
  BooleanInput,
  SimpleFormIterator,
  ArrayInput,
  FormDataConsumer,
} from "react-admin";
import { Grid } from "@mui/material";
import { GenderOptionsId, IncomingStudentGenderOptions } from "utils/choices";
import AttachmentInput from "server/components/Fields/AttachmentInput";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { settings } from "app";
import { SectionTitle } from "features/SectionTitle";
import { ErasmusIncomingType } from "utils/constants";
import { erasmusRequests } from "api/erasmusApi";
import { studentRequests } from "api/studentApi";
import { administrationRequests } from "api/administrationApi";
import { nameLatinRegexValidator, validateStart, validateEnd } from "utils/validations";

const CreateForm = ({
  isDisable,
  setIsDisable,
  isMobile,

  type,
}) => {
  const { watch, setValue } = useFormContext();

  const specialityId = watch("SpecialityId");
  const curriculumId = watch("CurriculumId");
  const isBip = watch("IsBip");

  const validateStartDate = (value, allValues) => validateStart(value, allValues.EndDate, true);
  const validateEndDate = (value, allValues) => validateEnd(value, allValues.StartDate, true);
  const validateBipStartDate = (value, allValues) => validateStart(value, allValues.BipEndDate, true);
  const validateBipEndDate = (value, allValues) => validateEnd(value, allValues.BipStartDate, true);

  useEffect(() => {
    setValue("curriculumId", null);
  }, [specialityId, setValue]);

  return (
    <Grid
      container
      spacing={2}
      sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
    >
      <Grid
        item
        xs={12}
      >
        <SectionTitle
          title="Лични данни"
          paddingY={0}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <TextInput
          label="Име"
          source="FirstName"
          validate={[required("Задължително"), maxLength(50)]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <TextInput
          label="Фамилия"
          source="LastName"
          validate={[required("Задължително"), maxLength(50)]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <TextInput
          label="Име (латиница)"
          source="FirstNameLatin"
          validate={[required("Задължително"), maxLength(50), nameLatinRegexValidator]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <TextInput
          label="Фамилия (латиница)"
          source="LastNameLatin"
          validate={[required("Задължително"), maxLength(50), nameLatinRegexValidator]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <SelectInput
          label="Пол"
          source="Gender"
          choices={type === ErasmusIncomingType.Regular ? IncomingStudentGenderOptions : GenderOptionsId}
          validate={required("Задължително")}
          fullWidth
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <ReferenceInput
          source="CountryId"
          reference={administrationRequests.getCountriesSelect()}
        >
          <AutocompleteInput
            label="Националност"
            optionText="nameBg"
            optionValue="id"
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            validate={required("Задължително")}
            fullWidth
          />
        </ReferenceInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <TextInput
          label="Имейл"
          source="Email"
          validate={[required("Задължително"), email(), maxLength(100)]}
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        xl={3}
      >
        <CustomDateInput
          source="BirthDate"
          label="Дата на раждане"
          validate={[required("Задължително")]}
          required
        />
      </Grid>
      <Grid
        item
        xs={12}
      >
        <SectionTitle paddingY={2} title={"Образование"} />
      </Grid>

      <>
        <Grid
          item
          xs={12}
          md={3}
        >
          <ReferenceInput
            source="SpecialityId"
            reference={administrationRequests.getSpecialitiesSelect()}
            filter={{
              filterByUser: true,
            }}
          >
            <SelectInput label="Специалност" />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <ReferenceInput
            source="InstructionLanguageId"
            reference={administrationRequests.getInstructionLanguagesSelect()}
          >
            <SelectInput
              label="Език на обучение"
              helperText={false}
              validate={required()}
              readOnly={isDisable}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <ReferenceInput
            source="AcademicYearId"
            reference={studentRequests.getAcademicYearsSelect()}
          >
            <SelectInput
              label="Учебна година"
              helperText={false}
              validate={required()}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <CustomDateInput
            source="StartDate"
            label="Начало на обучението"
            validate={[required("Задължително"), validateStartDate]}
            required
            readOnly={isDisable}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <CustomDateInput
            source="EndDate"
            label="Край на обучението"
            validate={[required("Задължително"), validateEndDate]}
            required
            readOnly={isDisable}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <TextInput
            label="Координатор - име"
            source="CoordinatorName"
            validate={[required("Задължително"), maxLength(100)]}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <TextInput
            label="Координатор - имейл"
            source="CoordinatorEmail"
            validate={[required("Задължително"), email(), maxLength(100)]}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <TextInput
            label="Изпращащ факултет"
            source="SourceFaculty"
            validate={[required("Задължително"), maxLength(100)]}
          />
        </Grid>
        <Grid
          item
          xs={12}
        >
          <BooleanInput
            source="IsBip"
            label="BIP"
            sx={{ width: "fit-content" }}
          />
        </Grid>
        {isBip ? (
          <>
            <Grid
              item
              xs={12}
              md={3}
            >
              <TextInput
                label="BIP име"
                source="BipName"
                validate={required("Задължително")}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <TextInput
                label="BIP номер"
                source="BipNumber"
                validate={required("Задължително")}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <CustomDateInput
                source="BipStartDate"
                label="BIP начална дата"
                validate={[required("Задължително"), validateBipStartDate]}
                required
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
            >
              <CustomDateInput
                source="BipEndDate"
                label="BIP крайна дата"
                validate={[required("Задължително"), validateBipEndDate]}
                required
              />
            </Grid>
          </>
        ) : null}
        {specialityId ? (
          <Grid
            item
            xs={12}
            md={3}
          >
            <ReferenceInput
              source="CurriculumId"
              reference={studentRequests.getCurriculumsSelect()}
              filter={{ specialityId: specialityId }}
            >
              <AutocompleteInput
                filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                label="Учебен план"
                optionText="name"
                disabled={isDisable}
                fullWidth
              />
            </ReferenceInput>
          </Grid>
        ) : null}
        {curriculumId && !isBip ? (
          <ArrayInput
            label="Дисциплини"
            source="Disciplines"
            defaultValue={[{}]}
          >
            <SimpleFormIterator>
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <ReferenceInput
                    source="DisciplineId"
                    reference={studentRequests.getCurriculumDisciplinesSelect(curriculumId)}
                  >
                    <AutocompleteInput
                      validate={required()}
                      filterToQuery={q => (q?.length > 2 ? { disciplineName: q } : {})}
                      optionText="name"
                      label="Дисциплина"
                      fullWidth
                    />
                  </ReferenceInput>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <FormDataConsumer>
                    {({ scopedFormData }) => {
                      const disciplineId = scopedFormData?.DisciplineId;

                      return disciplineId ? (
                        <ReferenceInput
                          source="semester"
                          reference={studentRequests.getCurriculumDisciplineSemestersSelect(curriculumId, disciplineId)}
                        >
                          <SelectInput
                            validate={required()}
                            optionText={"name"}
                            optionValue={"id"}
                            label="Семестър"
                            fullWidth
                          />
                        </ReferenceInput>
                      ) : null;
                    }}
                  </FormDataConsumer>
                </Grid>
              </Grid>
            </SimpleFormIterator>
          </ArrayInput>
        ) : null}
      </>

      <Grid
        item
        xs={12}
        md={3}
      >
        <ReferenceInput
          source="CycleOfEducation"
          reference={erasmusRequests.getCycleOfEducationSelect()}
        >
          <AutocompleteInput
            validate={required()}
            filterToQuery={q => (q?.length > 2 ? { disciplineName: q } : {})}
            optionText="nameBg"
            label="Цикъл на обучение"
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <TextInput
          minRows={5}
          maxRows={5}
          label="Мотивационно писмо"
          source="ShortMotivationLetter"
          multiline
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={3}
      >
        <ReferenceInput
          source="UniversityId"
          reference={administrationRequests.getUniversitiesSelect()}
        >
          <AutocompleteInput
            validate={required()}
            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
            optionText="name"
            label="Университет"
            fullWidth
          />
        </ReferenceInput>
      </Grid>
      <Grid
        item
        xs={12}
      >
        <BooleanInput
          source="WantsDormitory"
          label="Желае общежитие"
          sx={{ width: "fit-content" }}
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <AttachmentInput
          source="CvFile"
          label="CV"
          isRequired
        />
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
      >
        <AttachmentInput
          source="PhotoFile"
          label="Снимка"
          isRequired
        />
      </Grid>
      <Grid
        item
        xs={12}
        md={4}
      >
        <AttachmentInput
          source="MotivationLetterFile"
          label="Файл - мотивационно писмо"
          isRequired
        />
      </Grid>
    </Grid>
  );
};
export default CreateForm;
